/**
 * Query + mutation hooks for the payment banks shown on the customer app's
 * bank-transfer screen. Backed by /api/back-office/banks (BACK_OFFICE role).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface PaymentBank {
  id: number;
  bankCode: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  sortOrder: number;
  active: boolean;
}

export interface PaymentBankInput {
  bankCode: string;
  bankName: string;
  accountName: string;
  accountNumber: string;
  sortOrder?: number;
  active?: boolean;
}

interface ApiEnvelope<T> {
  status: string;
  message?: string;
  data: T;
}

const KEY = ['back-office', 'banks'];

export function useBanks() {
  return useQuery<PaymentBank[]>({
    queryKey: KEY,
    queryFn: async () => {
      const r = await api.get<ApiEnvelope<PaymentBank[]>>('/back-office/banks');
      return r.data.data;
    },
  });
}

export function useCreateBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PaymentBankInput) => {
      const r = await api.post<ApiEnvelope<PaymentBank>>('/back-office/banks', input);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: number; input: PaymentBankInput }) => {
      const r = await api.put<ApiEnvelope<PaymentBank>>(`/back-office/banks/${vars.id}`, vars.input);
      return r.data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeleteBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/back-office/banks/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
